import pick from 'lodash/pick'
import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { SEARCH, SearchContext } from '../search/SearchContext'
import { Button } from '../common/forms'
import BookingFields from '../bookings/BookingFields'
import PaymentFields from '../bookings/PaymentFields'
import { postBooking } from '../helpers/hotelHelper'
import {
  isPrePayUser,
  isCreditLimitUser,
  isTourOperator,
  isBookingAgent,
  isBookingAgentAndisBookingAnUpgrade,
  isBookingAgentAndisBookingAfresh,
  fetchCurrentUser,
  shouldDisplayPaymentsForTourOperatorOrBookingAgent
} from '../helpers/userHelper'
import DisplayErrors from '../common/forms/DisplayErrors'

import { fetchZohoLead } from "../api/zohoLead"

const HotelBookingForm = ({ successHandler, cancelHandler, tourOperatorNotes, setFormSubmitting }) => {
  const { state, dispatch } = useContext(SearchContext)
  const _setUser = (payload) => dispatch({ type: SEARCH.USER.SET, payload })
  const _setZohoLead = (payload) => dispatch({ type: SEARCH.ZOHO_LEAD.SET, payload })

  const [errors, setErrors] = useState({})
  const [submissionErrors, setSubmissionErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [valid, setValid] = useState(false)
  const [useExistingCard, setUseExistingCard] = useState(
    (isTourOperator(state.user) || isBookingAgent(state.user)) ? false : !!state.user.credit_card_last_four
  )
  const [saveCard, setSaveCard] = useState(true)
  const [addSurchargeToCreditLine, setAddSurchargeToCreditLine] = useState(false)
  const [chargePaymentToCreditLine, setChargePaymentToCreditLine] = useState(false)
  const [upgradeType, setUpgradeType] = useState('')
  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    billingStreet: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    spouseFirstName: '',
    spouseLastName: '',
  })

  const guestDetailValidationFields = ['firstName', 'lastName', 'email', 'phone']
  const cardPaymentValidationFields = ['cardName', 'cardNumber', 'expiryDate', 'cvc']
  const addressValidationFields = ['billingStreet', 'billingCity', 'billingState', 'billingZipCode']
  // const tourValidationFields = ['spouseFirstName', 'spouseLastName']
  const existingCardValidationFields = ['cvc']

  useEffect(() => {
    if (isBookingAgent(state.user) && state.zohoLead) {
      const nextValues = { ...values }
      const mappings = {
        firstName: state.zohoLead.first_name,
        lastName: state.zohoLead.last_name,
        spouseFirstName: state.zohoLead.spouse_first_name,
        spouseLastName: state.zohoLead.spouse_last_name,
        email: state.zohoLead.email,
        phone: state.zohoLead.phone,
        cardName: state.zohoLead.credit_card_name,
        cardNumber: state.zohoLead.credit_card_number,
        expiryDate: state.zohoLead.expiration_date,
        billingStreet: state.zohoLead.billing_street,
        billingCity: state.zohoLead.billing_city,
        billingZipCode: state.zohoLead.billing_zip_code,
        billingState: state.zohoLead.billing_state,
      }

      for (const [key, val] of Object.entries(mappings)) {
        if (val && val.length > 0) {
          nextValues[key] = val
        }
      }
      setValues(nextValues)
    }
  }, [state.zohoLead])

  useEffect(() => _setFormErrors(), [submissionErrors])

  const _refetchHandler = (e) => {
    e.preventDefault()
    __getZohoLead()
  }

  const __getZohoLead = async () => {
    const data = await fetchZohoLead(state.leadId)
    if (data.status === 200) {
      _setZohoLead(data.data.lead)
    } else {
      console.log('i have erred')
    }
  }


  const _setFormErrors = () => {
    if(submissionErrors.errors) {
      let subErrors = {}
      if( "payment" in submissionErrors.errors ) {
        submissionErrors.errors.payment.map((error) => {
          if(error == "Year expired") {
            subErrors.expiryDate = true
          }
          // Add more errors here
        })
      }
      if( Object.keys(subErrors).length > 0 ) {
        let comboErrors = Object.assign({}, errors, subErrors)
        setErrors(comboErrors)
      }
    }
  }

  const _validationFieldsForRegularUsers = () => {
    if (isCreditLimitUser(state.user)) {
      // Validate personal detail
      return pick(values, guestDetailValidationFields)
    }

    // Prepay user
    if (useExistingCard) {
      // Validate personal detail + CVC
      return pick(values, [...guestDetailValidationFields, ...existingCardValidationFields])
    } else {
      // Validate personal detail + payment detail + address
      return pick(values, [...guestDetailValidationFields, ...cardPaymentValidationFields, ...addressValidationFields])
    }
  }

  const _validationFieldsForBookingAgentsOrTourOperators = () => {
    let validationFields

    if (isPrePayUser(state.user)) {
      // Validate personal details + payment info + address
      validationFields = pick(values, [...guestDetailValidationFields, ...cardPaymentValidationFields, ...addressValidationFields])
    } else {
      // Credit Limit

      // Booking Agent Making a new booking(No Zoho lead)
      if (isBookingAgentAndisBookingAfresh(state)) {

        if (chargePaymentToCreditLine) {
          // Validate personal details
          validationFields = pick(values, guestDetailValidationFields)
        } else {
          // Validate personal details + payment info + address
          validationFields = pick(values, [...guestDetailValidationFields, ...cardPaymentValidationFields, ...addressValidationFields])
        }
      } else {
        // Booking Agent charging Upgrade Or Tour Operator
        if (!state.booking.room.rates[0].surcharge || (state.booking.room.rates[0].surcharge && addSurchargeToCreditLine)) {
          // Validate personal details
          validationFields = pick(values, guestDetailValidationFields)
        } else {
          // Validate personal details + payment info + address
          validationFields = pick(values, [...guestDetailValidationFields, ...cardPaymentValidationFields, ...addressValidationFields])
        }
      }
    }

    if (state.booking.action === 'book_room_and_tour') {
      let tourFields = pick(values, addressValidationFields)
      validationFields = { ...validationFields, ...tourFields }
    }

    if (isBookingAgentAndisBookingAnUpgrade(state) && state.booking.room.rates[0].surcharge) {
      validationFields = { ...validationFields, upgradeType }
    }

    return validationFields
  }

  const _validate = () => {
    let noErrors = Object.keys(errors).every((key) => errors[key] === undefined)
    let validationFields

    if (isTourOperator(state.user) || isBookingAgent(state.user)) {
      validationFields = _validationFieldsForBookingAgentsOrTourOperators()
    } else {
      validationFields = _validationFieldsForRegularUsers()
    }

    let hasValues = Object.keys(validationFields).every(
      (key) => validationFields[key].length > 0
    )
    setValid(noErrors && hasValues)
  }

  const _toggleSaveCard = () => setSaveCard(!saveCard)

  const _toggleAddSurchargeToCreditLine = () =>
    setAddSurchargeToCreditLine(!addSurchargeToCreditLine)

  const _toggleChargePaymentToCreditLine = () =>
    setChargePaymentToCreditLine(!chargePaymentToCreditLine)

  const _submitBooking = async (payload) => {
    try {

      await postBooking(
        payload,
        isPrePayUser(state.user),
        isTourOperator(state.user),
        useExistingCard,
        saveCard,
        addSurchargeToCreditLine,
        chargePaymentToCreditLine,
        upgradeType,
        tourOperatorNotes,
        state
      )
      successHandler(true)
      const userData = await fetchCurrentUser()
      if (userData) {
        _setUser(userData)
      }

    } catch (e) {
      let errors = {}
      let submission = "There was an error with your submission"

      try {
        if("response" in e && "data" in e.response) {
          // if rails returns 500 error, response is html
          if(e.response.status == 500) {
            console.error("e.response ", e.response) //eslint-disable-line
            errors = { 500: "There was an error with your submission, please try again" }
          } else {
            errors = "errors" in e.response.data ? e.response.data.errors : e.response.statusText
            submission = "message" in e.response.data ? e.response.data.message : submission
          }
        }
      } catch(e) {
        // do nothing, continue below
        errors = { unknown: "There was an error with your submission, please try again"}
      }

      console.error(errors) //eslint-disable-line

      setSubmissionErrors({
        submission: submission,
        errors: errors,
      })
      setSubmitting(false)
      setFormSubmitting(false)
    }
  }

  const _submitHandler = (e) => {
    if (e) {
      e.preventDefault()
      setSubmitting(true)
    }

    if (!submitting || !valid) return

    let dates = {
      arrival: state.searchedArrival,
      nights: state.searchedNights,
    }

    setFormSubmitting(true)

    _submitBooking({ values, booking: state.booking, dates })
  }

  useEffect(_submitHandler, [submitting])
  useEffect(_validate, [values, errors, addSurchargeToCreditLine, chargePaymentToCreditLine, upgradeType])

  const _inputHandler = (e) => {
    const { value, name, error } = e.currentTarget

    const nextValues = { ...values }
    nextValues[name] = value

    const nextErrors = { ...errors }
    nextErrors[name] = error

    setValues(nextValues)
    setErrors(nextErrors)
  }

  const _toggleUseExistingCard = () => {
    setUseExistingCard(!useExistingCard)
  }

  return (
    <form onSubmit={_submitHandler}>
      {submissionErrors.submission && <DisplayErrors errors={submissionErrors} />}

      <BookingFields
        handler={_inputHandler}
        values={values}
        errors={errors}
        user={state.user}
        useExistingCard={useExistingCard}
      />

      {(isPrePayUser(state.user) || shouldDisplayPaymentsForTourOperatorOrBookingAgent(state)) && (
        <PaymentFields
          handler={_inputHandler}
          values={values}
          errors={errors}
          showPaymentForm={isPrePayUser(state.user)}
          existingCard={state.user.credit_card_last_four}
          useExistingCard={useExistingCard}
          toggleUseExistingCard={_toggleUseExistingCard}
          saveCard={saveCard}
          toggleSaveCard={_toggleSaveCard}
          addSurchargeToCreditLine={addSurchargeToCreditLine}
          toggleAddSurchargeToCreditLine={_toggleAddSurchargeToCreditLine}
          chargePaymentToCreditLine={chargePaymentToCreditLine}
          toggleChargePaymentToCreditLine={_toggleChargePaymentToCreditLine}
          upgradeType={upgradeType}
          setUpgradeType={setUpgradeType}
          room={state.booking.room}
          user={state.user}
        />
      )}

      <Button
        className="secondary"
        clickHandler={_submitHandler}
        aria-label="Submit Form"
        disabled={submitting || !valid}
      >
        Submit
      </Button>

      {isBookingAgentAndisBookingAnUpgrade(state) && (
        <Button
          className="secondary"
          clickHandler={_refetchHandler}
          aria-label="Fetch Data"
          disabled={submitting}
        >
          Refetch Lead Data
        </Button>
      )}

      {cancelHandler && (
        <Button
          className="cancel"
          clickHandler={cancelHandler}
          aria-label="Cancel Form"
          disabled={submitting}
        >
          Cancel
        </Button>
      )}
    </form>
  )
}

HotelBookingForm.propTypes = {
  successHandler: PropTypes.func.isRequired,
  cancelHandler: PropTypes.func,
  tourOperatorNotes: PropTypes.string,
  setFormSubmitting: PropTypes.func
}

export default HotelBookingForm
